import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import FilePicker from '../../common/inputs/file-picker/FilerPicker';
import TextInput from '../../common/inputs/text-input/TextInput';
import DatePicker from '../../common/inputs/date-picker/DatePicker';
import Button from '../../common/button/Button';
import Image from '../../common/image/Image';
import AddRepresentativeModal from './add-representative-modal/AddRepresentativeModal';

import { ReactComponent as ProfileIcon } from '../../../resources/icons/profile.svg';
import { ReactComponent as EditIcon } from '../../../resources/icons/edit.svg';

import './my-profile.scss';

const MyProfile = ({
  details,
  profile,
  updateDetails,
  updateUsername,
  updateEmail,
  updateProfilePicture,
  addRepresentative,
}) => {
  const [profilePicture, setProfilePicture] = useState(undefined);
  const [editingUsername, setEditingUsername] = useState(false);
  const [username, setUsername] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [taxRegistrationNumber, setTaxRegistrationNumber] = useState('');
  const [incorporationDate, setIncorporationDate] = useState(undefined);
  const [importerCode, setImporterCode] = useState('');
  const [webURL, setWebURL] = useState('');
  const [tradeRating, setTradeRating] = useState('');
  const [addressLineOne, setAddressLineOne] = useState('');
  const [addressLineTwo, setAddressLineTwo] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [title, setTitle] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [profileRating, setProfileRating] = useState('');
  const [showAddRepresentativeModal, setShowAddRepresentativeModal] = useState(false);

  const fileRef = useRef(null);
  const initialUsername = useRef(null);
  const initialEmail = useRef(null);

  useEffect(() => {
    if (details) {
      setCompanyName(details.companyName ?? '');
      setCompanyRegistrationNumber(details.companyRegistrationNumber ?? '');
      setTradingName(details.tradingName ?? '');
      setTaxRegistrationNumber(details.taxRegistrationNumber ?? '');
      setIncorporationDate(details.incorporationDate ? new Date(details.incorporationDate) : null);
      setImporterCode(details.importerCode ?? '');
      setWebURL(details.webURL ?? '');
      setAddressLineOne(details.addressLine1 ?? '');
      setAddressLineTwo(details.addressLine2 ?? '');
      setCity(details.city ?? '');
      setProvince(details.province ?? '');
      setCountry(details.country ?? '');
      setPostalCode(details.postalCode ?? '');
      setTitle(details.title ?? '');
      setJobTitle(details.jobTitle ?? '');
      setFirstName(details.firstName ?? '');
      setLastName(details.lastName ?? '');
      setMobile(details.mobile ?? '');
    }
  }, [details]);

  useEffect(() => {
    if (profile) {
      setProfilePicture(profile.profilePictureExternalReference);
      setUsername(profile.username ?? '');
      initialUsername.current = profile.username ?? '';
      setEmail(profile.emailAddress ?? '');
      initialEmail.current = profile.emailAddress ?? '';
    }
  }, [profile]);

  const handleSaveClick = () => {
    const details = {
      companyName,
      companyRegistrationNumber,
      tradingName,
      taxRegistrationNumber,
      incorporationDate,
      importerCode,
      webURL,
      addressLine1: addressLineOne,
      addressLine2: addressLineTwo,
      city,
      province,
      country,
      postalCode,
      title,
      jobTitle,
      firstName,
      lastName,
      mobile,
    };

    updateDetails(details);
  };

  const handleEditUsernameClick = () => {
    if (!editingUsername) {
      setEditingUsername(true);
    } else if (username === initialUsername.current) {
      setEditingUsername(false);
    } else {
      updateUsername(username, () => setEditingUsername(false));
    }
  };

  const handleEditEmailClick = () => {
    if (!editingEmail) {
      setEditingEmail(true);
    } else if (email === initialEmail.current) {
      setEditingEmail(false);
    } else {
      updateEmail(email, () => setEditingEmail(false));
    }
  };

  const handleProfilePictureChange = (file) => {
    if (!file) return;
    updateProfilePicture(file);
  };

  const renderTable = () => (
    <table className="my-profile__profile__company-representatives__table" data-testid="representative-table">
      <thead className="my-profile__profile__company-representatives__table__header">
        <tr>
          <th>Job Title</th>
          <th>Title</th>
          <th>First Name</th>
          <th>Last Name</th>
          <th>Mobile</th>
          <th>Email</th>
        </tr>
      </thead>
      <tbody className="my-profile__profile__company-representatives__table__body">
        {details?.organisationRepresentatives.map((entry, index) => {
          const { jobTitle, title, firstName, lastName, mobile, email } = entry;

          return (
            <tr key={index}>
              <td>{jobTitle || '-'}</td>
              <td>{title || '-'}</td>
              <td>{firstName || '-'}</td>
              <td>{lastName || '-'}</td>
              <td>{mobile || '-'}</td>
              <td>{email || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <section className="my-profile" data-testid="my-profile">
        <section className="my-profile__account my-profile__card" data-testid="account-section">
          <p>My Account</p>
          <section className="my-profile__account__profile-picture">
            {profilePicture ? (
              <Image
                className="my-profile__account__profile-picture__image"
                key={profilePicture}
                externalReference={profilePicture}
                alt="Profile"
              />
            ) : (
              !profilePicture && <ProfileIcon data-testid="profile-picture-placeholder" />
            )}
            <FilePicker
              fileRef={fileRef}
              fileType="image/*"
              enableFileLimit={true}
              onChange={({ target }) => handleProfilePictureChange(target.files[0])}
              triggerElement={
                <button
                  className="my-profile__account__image-button"
                  onClick={() => fileRef.current.click()}
                  data-testid="edit-profile-picture-button"
                />
              }
            />
          </section>
          <section className="my-profile__account__details">
            <div className="my-profile__account__detail">
              {!editingUsername ? <p>Username</p> : <label htmlFor="username">Username</label>}
              {!editingUsername ? (
                <p>{username}</p>
              ) : (
                <TextInput
                  name="username"
                  value={username}
                  onChange={({ target }) => setUsername(target.value)}
                  autoFocus
                />
              )}
              <EditIcon
                className="my-profile__account__edit-icon"
                onClick={handleEditUsernameClick}
                data-testid="edit-username-icon"
              />
            </div>
            <div className="my-profile__account__detail">
              {!editingEmail ? <p>E-mail Address</p> : <label htmlFor="email">E-mail Address</label>}
              {!editingEmail ? (
                <p>{email}</p>
              ) : (
                <TextInput name="email" value={email} onChange={({ target }) => setEmail(target.value)} autoFocus />
              )}
              <EditIcon
                className="my-profile__account__edit-icon"
                onClick={handleEditEmailClick}
                data-testid="edit-email-icon"
              />
            </div>
          </section>
          <Link to={'/internal-password-reset'}>Change Password</Link>
        </section>

        <section className="my-profile__profile my-profile__card" data-testid="profile-section">
          <p>My Profile</p>
          <section
            className="my-profile__profile__company-information my-profile__card"
            data-testid="company-information-section"
          >
            <p>Company Information</p>
            <div className="my-profile__profile__company-information__fields">
              <div className="my-profile__profile__field">
                <label htmlFor="company-name">Company Name</label>
                <TextInput
                  name="company-name"
                  value={companyName}
                  onChange={({ target }) => setCompanyName(target.value)}
                  disabled={true}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="company-registration-number">Company Registration No.</label>
                <TextInput
                  name="company-registration-number"
                  value={companyRegistrationNumber}
                  onChange={({ target }) => setCompanyRegistrationNumber(target.value)}
                  disabled={true}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="trading-name">Trading Name</label>
                <TextInput
                  name="trading-name"
                  value={tradingName}
                  onChange={({ target }) => setTradingName(target.value)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="vat-number">Vat No.</label>
                <TextInput
                  name="vat-number"
                  value={taxRegistrationNumber}
                  onChange={({ target }) => setTaxRegistrationNumber(target.value)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="incorporation-date">Incorporation Date</label>
                <DatePicker
                  name="incorporation-date"
                  value={incorporationDate}
                  onChange={(date) => setIncorporationDate(date)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="importer-code">Importer Code</label>
                <TextInput
                  name="importer-code"
                  value={importerCode}
                  onChange={({ target }) => setImporterCode(target.value)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="web-url">Web URL</label>
                <TextInput name="web-url" value={webURL} onChange={({ target }) => setWebURL(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="trade-rating">Trade Rating</label>
                <TextInput
                  name="trade-rating"
                  value={tradeRating}
                  onChange={({ target }) => setTradeRating(target.value)}
                />
              </div>
            </div>
          </section>

          <section
            className="my-profile__profile__company-contact-information my-profile__card"
            data-testid="company-contact-information-section"
          >
            <p>Company Contact Information</p>
            <div className="my-profile__profile__company-contact-information__fields">
              <div className="my-profile__profile__field">
                <label htmlFor="address-line-one">Address Line 1</label>
                <TextInput
                  name="address-line-one"
                  value={addressLineOne}
                  onChange={({ target }) => setAddressLineOne(target.value)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="province">Province/Region/State</label>
                <TextInput name="province" value={province} onChange={({ target }) => setProvince(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="address-line-two">Address Line 2</label>
                <TextInput
                  name="address-line-two"
                  value={addressLineTwo}
                  onChange={({ target }) => setAddressLineTwo(target.value)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="country">Country</label>
                <TextInput name="country" value={country} onChange={({ target }) => setCountry(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="city">City</label>
                <TextInput name="city" value={city} onChange={({ target }) => setCity(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="postal-code">Postal Code</label>
                <TextInput
                  name="postal-code"
                  value={postalCode}
                  onChange={({ target }) => setPostalCode(target.value)}
                />
              </div>
            </div>
          </section>

          <section
            className="my-profile__profile__personal-information my-profile__card"
            data-testid="personal-information-section"
          >
            <p>Personal Information</p>
            <div className="my-profile__profile__personal-information__fields">
              <div className="my-profile__profile__field">
                <label htmlFor="job-title">Job Title</label>
                <TextInput name="job-title" value={jobTitle} onChange={({ target }) => setJobTitle(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="title">Title</label>
                <TextInput name="title" value={title} onChange={({ target }) => setTitle(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="first-name">First Name</label>
                <TextInput name="first-name" value={firstName} onChange={({ target }) => setFirstName(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="last-name">Last Name</label>
                <TextInput name="last-name" value={lastName} onChange={({ target }) => setLastName(target.value)} />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="profile-rating">Profile Rating</label>
                <TextInput
                  name="profile-rating"
                  value={profileRating}
                  onChange={({ target }) => setProfileRating(target.value)}
                />
              </div>
              <div className="my-profile__profile__field">
                <label htmlFor="mobile">Mobile</label>
                <TextInput name="mobile" value={mobile} onChange={({ target }) => setMobile(target.value)} />
              </div>
            </div>
          </section>

          <section
            className="my-profile__profile__company-representatives my-profile__card"
            data-testid="company-representatives-section"
          >
            <p>Company Representatives</p>
            {renderTable()}
            <Button
              className="my-profile__profile__company-representatives__button"
              onClick={() => setShowAddRepresentativeModal(true)}
            >
              ADD REPRESENTATIVE
            </Button>
          </section>

          <Button onClick={handleSaveClick}>Save Changes</Button>
        </section>
      </section>

      <AddRepresentativeModal
        show={showAddRepresentativeModal}
        setShow={setShowAddRepresentativeModal}
        addRepresentative={addRepresentative}
      />
    </>
  );
};

MyProfile.propTypes = {
  details: PropTypes.shape({}),
  profile: PropTypes.shape({}),
  updateDetails: PropTypes.func.isRequired,
  updateUsername: PropTypes.func.isRequired,
  updateEmail: PropTypes.func.isRequired,
  updateProfilePicture: PropTypes.func.isRequired,
  addRepresentative: PropTypes.func.isRequired,
};

MyProfile.defaultProps = {
  details: undefined,
  profile: undefined,
};

export default MyProfile;
